import { ProtectedRoute, RequireAbility } from "@amazing-care/auth";
import { createBrowserRouter, Navigate } from "react-router";
import { AppShellSkeleton } from "@/components/app-shell-skeleton";
import { PageSkeleton } from "@/components/page-skeleton";
import { RouteError } from "@/components/route-error";
import { RestrictedAccess } from "@/features/admin/components/restricted-access";
import { NoAccess } from "@/features/auth/components/no-access";
import { AuthLayout } from "@/layouts/auth-layout";
import { StaffLayout } from "@/layouts/staff-layout";
import { lazyPage, PAGES } from "@/route-modules";
import { ROUTE_PATTERNS, ROUTES } from "@/routes";

/**
 * Single source of routes. Every page is code-split; the admin subtree carries
 * an extra role gate on top of the auth gate.
 */
export const router = createBrowserRouter([
  {
    errorElement: <RouteError />,
    children: [
      { index: true, element: <Navigate to={ROUTES.staff} replace /> },
      {
        element: <AuthLayout />,
        children: [
          {
            path: ROUTES.login,
            lazy: lazyPage(PAGES.login),
          },
          {
            path: ROUTES.authCallback,
            lazy: lazyPage(PAGES.authCallback),
          },
          {
            path: ROUTES.teamsSignInStart,
            lazy: lazyPage(PAGES.teamsSignInStart),
          },
          {
            path: ROUTES.teamsSignInEnd,
            lazy: lazyPage(PAGES.teamsSignInEnd),
          },
        ],
      },
      {
        element: <ProtectedRoute noAccess={<NoAccess />} loading={<AppShellSkeleton />} />,
        children: [
          {
            // Outside StaffLayout: the guide brings its own Fumadocs sidebar.
            path: ROUTE_PATTERNS.guide,
            lazy: lazyPage(PAGES.guide),
          },
          {
            element: <StaffLayout />,
            children: [
              {
                path: ROUTES.staff,
                lazy: lazyPage(PAGES.staffHome),
              },
              {
                path: ROUTE_PATTERNS.department,
                lazy: lazyPage(PAGES.department),
              },
              {
                path: ROUTE_PATTERNS.document,
                lazy: lazyPage(PAGES.document),
              },
              {
                path: ROUTE_PATTERNS.activity,
                lazy: lazyPage(PAGES.activity),
              },
              {
                element: (
                  <RequireAbility
                    I="manage"
                    a="Manager"
                    fallback={<RestrictedAccess />}
                    loading={<PageSkeleton />}
                  />
                ),
                children: [
                  {
                    path: ROUTES.adminUsers,
                    lazy: lazyPage(PAGES.adminUsers),
                  },
                ],
              },
              {
                element: (
                  <RequireAbility
                    I="manage"
                    a="Announcement"
                    fallback={<RestrictedAccess />}
                    loading={<PageSkeleton />}
                  />
                ),
                children: [
                  {
                    path: ROUTES.adminContent,
                    lazy: lazyPage(PAGES.adminContent),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "*",
        lazy: lazyPage(PAGES.notFound),
      },
    ],
  },
]);
